export type MachineRunFinishWindow =
  | "in_30_40_minutes"
  | "in_20_30_minutes"
  | "in_10_20_minutes"
  | "in_5_10_minutes";

export type MachineRunProgressStep = 0 | 1 | 2 | 3 | 4;

export type MachineRunProgressInput = {
  startedAt: string | Date | null | undefined;
  machineType: "mosogep" | "szaritogep" | "ozon";
  moxaOperatingType?: number | null;
  defaultDryerCycleCount?: number | null;
  extraDryerCycleCount?: number | null;
  now?: Date;
};

export const machineRunFinishWindowOrder: readonly MachineRunFinishWindow[] = [
  "in_30_40_minutes",
  "in_20_30_minutes",
  "in_10_20_minutes",
  "in_5_10_minutes",
];

export const AVERAGE_WASH_DURATION_MINUTES = 35;
export const STANDARD_DRYER_DURATION_MINUTES = 37;

export function getMachineRunDurationMinutes(input: {
  machineType: "mosogep" | "szaritogep" | "ozon";
  moxaOperatingType?: number | null;
  defaultDryerCycleCount?: number | null;
  extraDryerCycleCount?: number | null;
}) {
  if (input.machineType !== "szaritogep") {
    return AVERAGE_WASH_DURATION_MINUTES;
  }

  if (input.moxaOperatingType !== 1) {
    return STANDARD_DRYER_DURATION_MINUTES;
  }

  if (input.extraDryerCycleCount == null) {
    return STANDARD_DRYER_DURATION_MINUTES;
  }

  return (input.defaultDryerCycleCount ?? 1) + input.extraDryerCycleCount * 12;
}

export function getMachineRunFinishWindow(
  remainingMinutes: number,
): MachineRunFinishWindow {
  if (remainingMinutes > 30) {
    return "in_30_40_minutes";
  }

  if (remainingMinutes > 20) {
    return "in_20_30_minutes";
  }

  if (remainingMinutes > 10) {
    return "in_10_20_minutes";
  }

  return "in_5_10_minutes";
}

export function getMachineRunProgressStep(
  finishWindow: MachineRunFinishWindow,
): Exclude<MachineRunProgressStep, 0> {
  return (
    machineRunFinishWindowOrder.indexOf(finishWindow) + 1
  ) as Exclude<MachineRunProgressStep, 0>;
}

export function getMachineRunProgressIndex(
  finishWindow: MachineRunFinishWindow,
) {
  return machineRunFinishWindowOrder.indexOf(finishWindow);
}

export function clampMachineRunProgressStep(
  progressStep: number | null | undefined,
): MachineRunProgressStep {
  if (!Number.isFinite(progressStep)) {
    return 0;
  }

  const safeProgressStep = Number(progressStep);

  return Math.max(0, Math.min(4, Math.round(safeProgressStep))) as MachineRunProgressStep;
}

export function getMachineRunProgressStepFromStart(
  input: MachineRunProgressInput,
): MachineRunProgressStep {
  if (!input.startedAt) {
    return 0;
  }

  const startedAt = new Date(input.startedAt);

  if (Number.isNaN(startedAt.getTime())) {
    return 0;
  }

  const now = input.now ?? new Date();
  const durationMinutes = getMachineRunDurationMinutes(input);
  const elapsedMinutes = (now.getTime() - startedAt.getTime()) / 60_000;

  return getMachineRunProgressStep(
    getMachineRunFinishWindow(durationMinutes - elapsedMinutes),
  );
}
